/**
 * The typed API client, and the one place an error becomes something a
 * component can render.
 *
 * Every route answers `{ ok: true, ... }` or `{ ok: false, reason }`, so this
 * wrapper has exactly one job: turn the second into a thrown `ApiError` with
 * the sentence the worker wrote, and return the first with its own type. No
 * component ever reads a status code.
 */

import {
  HEIGHT_PART,
  INVENTORY_HEADER,
  PHOTO_PART,
  ROUTES,
  THUMB_PART,
  WIDTH_PART,
  itemPath,
  fieldTemplatePath,
  inventoryMemberPath,
  itemPhotosPath,
  passkeyPath,
  photoPath,
  savedSearchPath,
  type AdminAccount,
  type AdminSummary,
  type AllowlistEntry,
  type ApiError as ApiErrorBody,
  type CategoryCount,
  type FieldTemplate,
  type FieldTemplateCreateRequest,
  type HealthReport,
  type InventoryMember,
  type InventoryMeta,
  type InventoryReport,
  type ConvertResponse,
  type MergeUnitsRequest,
  type SplitLineRequest,
  type ReportFacts,
  type ReportSpec,
  type SavedReport,
  type Item,
  type ItemPage,
  type ItemQuery,
  type ItemWriteRequest,
  type LocationCount,
  type LocationUpdateRequest,
  type PasskeyCreationOptions,
  type PasskeyCreationResponse,
  type PasskeyRequestOptions,
  type PasskeyRequestResponse,
  type PasskeySummary,
  type PhotoMeta,
  type SavedSearch,
  type SessionUser,
  type TagCount,
  ATTACHMENT_PART,
  attachmentPath,
  clientPath,
  exportTemplatePath,
  itemStockPath,
  inventoryPath,
  receiptAttachmentPath,
  retirementCertificatePath,
  type AttachmentMeta,
  type BillingLine,
  type Client,
  type ClientColor,
  type ClientWriteRequest,
  deploymentPath,
  type DeploymentSpotRequest,
  type ReserveTagRenameRequest,
  type DeployInput,
  type Deployment,
  type ExportTemplate,
  type ExportTemplateSpec,
  type InventoryKind,
  type InventoryMembership,
  type Receipt,
  type ReceiptInput,
  type RetireInput,
  type Retirement,
  type ReturnInput,
  type TransferInput,
  type StockDetail,
  type StockOverview,
  type StockRecords,
  type InventoriesResponse,
  type InventoryRenameRequest,
  type ShelveInput,
  type StockSettings,
  type PriceFromCostRequest,
  type PriceFromCostResponse,
  releasePath,
  reservePath,
  reserveTagPath,
  type AlertPrefs,
  type HoldInput,
  type QuietInput,
  type ReleaseInput,
  type Reserve,
  type ReserveDetail,
  type ReserveInput,
  type ReserveTag,
} from "@iltocat/shared";

import { getActiveInventory } from "./activeInventory.js";

export class ApiError extends Error {
  readonly status: number;
  /** Named bindings or secrets, when the worker says it is not configured. */
  readonly missing: string[] | undefined;

  constructor(reason: string, status: number, missing?: string[]) {
    super(reason);
    this.name = "ApiError";
    this.status = status;
    this.missing = missing;
  }

  /** True when the answer was "sign in first", which every screen handles the same way. */
  get needsSignIn(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  const activeInventory = getActiveInventory();
  try {
    response = await fetch(path, {
      // Same origin, so the session cookie rides along without anything else
      // being configured -- see docs/notes/deploying.md on why the app and the
      // API are one worker.
      credentials: "same-origin",
      ...init,
      headers: {
        Accept: "application/json",
        // Sent on every call, not only inventory ones -- harmless where the
        // worker never reads it, and the one place this module has to decide
        // whether a request is "inventory-shaped" is nowhere, which is the
        // point.
        ...(activeInventory ? { [INVENTORY_HEADER]: activeInventory } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    // A dropped connection is the one failure the worker never gets to explain.
    throw new ApiError("Could not reach utucat. Check your connection.", 0);
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || (payload as { ok?: boolean })?.ok === false) {
    const error = payload as ApiErrorBody | null;
    throw new ApiError(error?.reason ?? "Something went wrong.", response.status, error?.missing);
  }

  return payload as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

const remove = <T>(path: string): Promise<T> => request<T>(path, { method: "DELETE" });

/**
 * A multipart body sets its own Content-Type, boundary and all. Setting one
 * here produces a request the worker cannot parse, and the error it produces
 * ("that upload was not readable") says nothing about why.
 */
const sendForm = <T>(path: string, form: FormData): Promise<T> =>
  request<T>(path, { method: "POST", body: form });

/**
 * A photo's raw bytes, for a client-side zip (the Stage 4 listing screen's
 * "download all" and Stage 5's export). Not `request()`: that assumes a JSON
 * body, and a photo is not one. An `<img src={photoPath(id)}>` can point
 * straight at the route and let the browser send the cookie itself, but a
 * fetch from code has to build the same headers `request()` does by hand --
 * this is the one place that does, rather than each call site repeating it.
 * The bug this fixes: a plain `fetch(photoPath(id))` with no inventory header
 * defaults to the caller's *own* inventory, so an editor downloading a shared
 * inventory's photos got a 404 for every one.
 */
export async function fetchPhoto(id: string): Promise<Uint8Array> {
  const activeInventory = getActiveInventory();
  let response: Response;
  try {
    response = await fetch(photoPath(id), {
      credentials: "same-origin",
      headers: activeInventory ? { [INVENTORY_HEADER]: activeInventory } : {},
    });
  } catch {
    throw new ApiError("Could not reach utucat. Check your connection.", 0);
  }
  if (!response.ok) throw new ApiError("A photo would not download.", response.status);
  return new Uint8Array(await response.arrayBuffer());
}

/**
 * A receipt's invoice or a retirement's certificate, as bytes to save. Not a
 * link: the route answers `Content-Disposition: attachment` anyway, and going
 * through code keeps the inventory header on it the same way `fetchPhoto()`
 * does.
 */
export async function fetchAttachment(id: string): Promise<Blob> {
  const activeInventory = getActiveInventory();
  let response: Response;
  try {
    response = await fetch(attachmentPath(id), {
      credentials: "same-origin",
      headers: activeInventory ? { [INVENTORY_HEADER]: activeInventory } : {},
    });
  } catch {
    throw new ApiError("Could not reach utucat. Check your connection.", 0);
  }
  if (!response.ok) throw new ApiError("That file would not download.", response.status);
  return response.blob();
}

const put = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

function attachmentForm(file: File): FormData {
  const form = new FormData();
  form.set(ATTACHMENT_PART, file, file.name);
  return form;
}

/** The query string a grid asks with. Anything absent is simply not sent. */
function itemsQuery(query: ItemQuery): string {
  const params = new URLSearchParams();
  if (query.locationId !== undefined) params.set("location", query.locationId ?? "none");
  if (query.categoryId !== undefined) params.set("category", query.categoryId ?? "none");
  if (query.needsDetails) params.set("needs", "1");
  if (query.photos) params.set("photo", query.photos);
  if (query.q) params.set("q", query.q);
  if (query.status) params.set("status", query.status);
  if (query.tagId) params.set("tag", query.tagId);
  if (query.deepLocation) params.set("deep", "1");
  if (query.stock) params.set("stock", query.stock);
  if (query.clientId) params.set("client", query.clientId);
  if (query.withRetired) params.set("retired", "1");
  if (query.sort) params.set("sort", query.sort);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit) params.set("limit", String(query.limit));
  const text = params.toString();
  return text ? `?${text}` : "";
}

export const api = {
  health: (): Promise<HealthReport> => request(ROUTES.health),

  me: (): Promise<{ user: SessionUser | null }> => request(ROUTES.me),

  signInLink: (email: string): Promise<{ sent: true }> => post(ROUTES.authStart, { email }),

  /** The confirm screen's button: redeems a mailed link, and the session cookie comes back with the answer. */
  confirmSignIn: (token: string): Promise<unknown> => post(ROUTES.authVerify, { token }),

  signOut: (): Promise<unknown> => post(ROUTES.logout),

  signOutEverywhere: (): Promise<unknown> => post(ROUTES.logoutEverywhere),

  passkeys: {
    list: (): Promise<{ passkeys: PasskeySummary[] }> => request(ROUTES.passkeys),

    remove: (id: string): Promise<{ passkeys: PasskeySummary[] }> => remove(passkeyPath(id)),

    registerOptions: (): Promise<{ options: PasskeyCreationOptions }> => post(ROUTES.passkeyRegisterOptions),

    registerVerify: (
      response: PasskeyCreationResponse,
      name: string,
    ): Promise<{ passkeys: PasskeySummary[] }> => post(ROUTES.passkeyRegisterVerify, { response, name }),

    loginOptions: (): Promise<{ options: PasskeyRequestOptions }> => post(ROUTES.passkeyLoginOptions),

    loginVerify: (response: PasskeyRequestResponse): Promise<{ user: SessionUser | null }> =>
      post(ROUTES.passkeyLoginVerify, { response }),
  },

  inventory: (): Promise<{ inventory: InventoryMeta }> => request(ROUTES.inventoryMeta),

  /**
   * The inventory being viewed when you own it, your own home inventory when
   * you are only an editor there -- see `members()` in
   * workers/api/src/inventory/members.ts.
   */
  sharing: {
    members: (): Promise<{ members: InventoryMember[] }> => request(ROUTES.inventoryMembers),
    invite: (email: string, role: "editor" | "viewer" = "editor"): Promise<{ members: InventoryMember[] }> =>
      post(ROUTES.inventoryMembers, { email, role }),
    remove: (userId: string): Promise<{ members: InventoryMember[] }> => remove(inventoryMemberPath(userId)),
  },

  items: {
    list: (query: ItemQuery = {}): Promise<ItemPage> => request(`${ROUTES.items}${itemsQuery(query)}`),

    get: (id: string): Promise<{ item: Item }> => request(itemPath(id)),

    create: (draft: ItemWriteRequest): Promise<{ item: Item }> => post(ROUTES.items, draft),

    update: (id: string, changes: ItemWriteRequest): Promise<{ item: Item }> => patch(itemPath(id), changes),

    remove: (id: string): Promise<{ usedBytes: number }> => remove(itemPath(id)),

    /**
     * One photo, already resized and re-encoded by `lib/photos.ts`. The
     * dimensions travel beside the bytes because the worker will not decode an
     * image to find out -- it stores what the browser says and refuses anything
     * outside the bounds both sides share.
     */
    addPhoto: (
      itemId: string,
      photo: { full: Blob; thumb: Blob; width: number; height: number },
    ): Promise<{ photo: PhotoMeta; usedBytes: number }> => {
      const form = new FormData();
      form.set(PHOTO_PART, photo.full, "photo.jpg");
      form.set(THUMB_PART, photo.thumb, "thumb.jpg");
      form.set(WIDTH_PART, String(photo.width));
      form.set(HEIGHT_PART, String(photo.height));
      return sendForm(itemPhotosPath(itemId), form);
    },

    /** The whole photo list, in the order it should show from now on -- cover first. */
    reorderPhotos: (itemId: string, order: string[]): Promise<{ photos: PhotoMeta[] }> =>
      patch(itemPhotosPath(itemId), { order }),
  },

  photos: {
    remove: (id: string): Promise<{ usedBytes: number }> => remove(photoPath(id)),
  },

  locations: {
    list: (): Promise<{ locations: LocationCount[] }> => request(ROUTES.locations),
    create: (name: string, parentId: string | null): Promise<{ locations: LocationCount[] }> =>
      post(ROUTES.locations, { name, parentId }),
    update: (id: string, change: LocationUpdateRequest): Promise<{ locations: LocationCount[] }> =>
      patch(`${ROUTES.locations}/${encodeURIComponent(id)}`, change),
    /** `into`: a location for its contents, null for the top level, left out for its parent. */
    remove: (id: string, into?: string | null): Promise<{ locations: LocationCount[] }> =>
      remove(
        `${ROUTES.locations}/${encodeURIComponent(id)}${
          into === undefined ? "" : `?into=${into === null ? "top" : encodeURIComponent(into)}`
        }`,
      ),
  },

  categories: {
    list: (): Promise<{ categories: CategoryCount[] }> => request(ROUTES.categories),
    create: (name: string): Promise<{ categories: CategoryCount[] }> => post(ROUTES.categories, { name }),
  },

  tags: {
    list: (): Promise<{ tags: TagCount[] }> => request(ROUTES.tags),
  },

  report: (): Promise<{ report: InventoryReport }> => request(ROUTES.report),

  /** Stage 6, reports you build: the rows they group, and the ones saved under a name. */
  reports: {
    facts: (from: string, to: string): Promise<{ facts: ReportFacts }> =>
      request(`${ROUTES.reportFacts}?from=${from}&to=${to}`),
    list: (): Promise<{ reports: SavedReport[] }> => request(ROUTES.reports),
    save: (name: string, spec: ReportSpec): Promise<{ reports: SavedReport[] }> =>
      post(ROUTES.reports, { name, spec }),
    remove: (id: string): Promise<{ reports: SavedReport[] }> =>
      remove(`${ROUTES.reports}/${encodeURIComponent(id)}`),
  },

  /** Stage 7. A new inventory the signed-in account owns. */
  createInventory: (
    name: string,
    kind: InventoryKind,
    currency?: string,
  ): Promise<{ inventoryId: string; memberships: InventoryMembership[] }> =>
    post(ROUTES.inventories, { name, kind, ...(currency ? { currency } : {}) }),

  /** Managing one already made. Each answers with the account's memberships, fresh. */
  inventories: {
    rename: (id: string, name: string): Promise<InventoriesResponse> => patch(inventoryPath(id), { name }),
    /** A name, a currency, or both. */
    update: (id: string, change: InventoryRenameRequest): Promise<InventoriesResponse> =>
      patch(inventoryPath(id), change),
    archive: (id: string): Promise<InventoriesResponse> => post(inventoryPath(id, "archive")),
    restore: (id: string): Promise<InventoriesResponse> => post(inventoryPath(id, "restore")),
    leave: (id: string): Promise<InventoriesResponse> => post(inventoryPath(id, "leave")),
    remove: (id: string): Promise<InventoriesResponse> => remove(inventoryPath(id)),
  },

  stock: {
    overview: (): Promise<StockOverview> => request(ROUTES.stock),
    records: (): Promise<StockRecords> => request(ROUTES.stockRecords),
    detail: (itemId: string): Promise<StockDetail> => request(itemStockPath(itemId)),
    shelve: (itemId: string, input: ShelveInput): Promise<StockDetail> => post(itemStockPath(itemId), input),
    settings: (): Promise<{ settings: StockSettings }> => request(ROUTES.stockSettings),
    saveSettings: (settings: StockSettings): Promise<{ settings: StockSettings }> =>
      put(ROUTES.stockSettings, settings),
    /** Price stock at cost plus the standard markup: unpriced items only, or every one. */
    priceFromCost: (overwrite: boolean, itemIds?: string[]): Promise<PriceFromCostResponse> =>
      post(ROUTES.stockPriceFromCost, {
        overwrite,
        ...(itemIds ? { itemIds } : {}),
      } satisfies PriceFromCostRequest),
    deploy: (input: DeployInput): Promise<{ deployments: Deployment[] }> => post(ROUTES.stockDeploy, input),
    return: (input: ReturnInput): Promise<{ deployment: Deployment }> => post(ROUTES.stockReturn, input),
    /** Straight from one client to another: returned and redeployed in one write. */
    transfer: (input: TransferInput): Promise<{ deployments: Deployment[] }> =>
      post(ROUTES.stockTransfer, input),
    retire: (input: RetireInput): Promise<{ retirement: Retirement }> => post(ROUTES.stockRetire, input),
    /** Serialized units into one counted line; the first id is kept. */
    merge: (itemIds: string[], on: string): Promise<ConvertResponse> =>
      post(ROUTES.stockMerge, { itemIds, on } satisfies MergeUnitsRequest),
    /** A counted line into one serialized item per unit. */
    split: (itemId: string, serials: string[], on: string): Promise<ConvertResponse> =>
      post(ROUTES.stockSplit, { itemId, serials, on } satisfies SplitLineRequest),
    /** Where at the client one deployment is; "" clears it. */
    setSpot: (deploymentId: string, spot: string): Promise<{ deployment: Deployment }> =>
      patch(deploymentPath(deploymentId), { spot } satisfies DeploymentSpotRequest),
    certificate: (
      retirementId: string,
      file: File,
    ): Promise<{ attachment: AttachmentMeta; usedBytes: number }> =>
      sendForm(retirementCertificatePath(retirementId), attachmentForm(file)),
  },

  clients: {
    list: (): Promise<{ clients: Client[] }> => request(ROUTES.clients),
    create: (name: string): Promise<{ clients: Client[] }> => post(ROUTES.clients, { name }),
    rename: (id: string, name: string): Promise<{ clients: Client[] }> => patch(clientPath(id), { name }),
    /** Null is the plain grey. */
    recolor: (id: string, color: ClientColor | null): Promise<{ clients: Client[] }> =>
      patch(clientPath(id), { color } satisfies ClientWriteRequest),
    remove: (id: string): Promise<{ clients: Client[] }> => remove(clientPath(id)),
  },

  receipts: {
    list: (): Promise<{ receipts: Receipt[] }> => request(ROUTES.receipts),
    create: (input: ReceiptInput): Promise<{ receipt: Receipt; itemIds: string[] }> =>
      post(ROUTES.receipts, input),
    attach: (receiptId: string, file: File): Promise<{ attachment: AttachmentMeta; usedBytes: number }> =>
      sendForm(receiptAttachmentPath(receiptId), attachmentForm(file)),
  },

  billing: {
    unbilled: (through: string): Promise<{ lines: BillingLine[] }> =>
      request(`${ROUTES.billing}?through=${encodeURIComponent(through)}`),
    mark: (deploymentIds: string[]): Promise<{ marked: number }> =>
      post(ROUTES.billingMark, { deploymentIds }),
    templates: (): Promise<{ templates: ExportTemplate[] }> => request(ROUTES.exportTemplates),
    saveTemplate: (spec: ExportTemplateSpec): Promise<{ templates: ExportTemplate[] }> =>
      post(ROUTES.exportTemplates, spec),
    removeTemplate: (id: string): Promise<{ templates: ExportTemplate[] }> => remove(exportTemplatePath(id)),
  },

  /** Back stock: reserves, what they hold, and this person's own alert settings. */
  reserves: {
    list: (): Promise<{ reserves: Reserve[]; alerts: AlertPrefs; reserveTags: ReserveTag[] }> =>
      request(ROUTES.reserves),
    detail: (id: string): Promise<ReserveDetail> => request(reservePath(id)),
    create: (input: ReserveInput): Promise<{ reserve: Reserve }> => post(ROUTES.reserves, input),
    update: (id: string, input: ReserveInput): Promise<{ reserve: Reserve }> => patch(reservePath(id), input),
    remove: (id: string, on: string): Promise<{ reserves: Reserve[] }> =>
      remove(`${reservePath(id)}?on=${encodeURIComponent(on)}`),
    hold: (id: string, input: HoldInput): Promise<ReserveDetail> => post(reservePath(id, "hold"), input),
    fill: (id: string, heldOn: string): Promise<ReserveDetail> => post(reservePath(id, "fill"), { heldOn }),
    release: (holdId: string, input: ReleaseInput): Promise<ReserveDetail> =>
      post(releasePath(holdId), input),
    quiet: (id: string, input: QuietInput): Promise<{ reserve: Reserve }> =>
      post(reservePath(id, "quiet"), input),
    saveAlerts: (prefs: AlertPrefs): Promise<{ alerts: AlertPrefs }> => put(ROUTES.reserveAlerts, prefs),
    tags: (): Promise<{ reserveTags: ReserveTag[] }> => request(ROUTES.reserveTags),
    createTag: (name: string): Promise<{ reserveTags: ReserveTag[] }> => post(ROUTES.reserveTags, { name }),
    /** Renamed on every item that wears it and every rule that matches on it. */
    renameTag: (name: string, to: string): Promise<{ reserveTags: ReserveTag[] }> =>
      patch(reserveTagPath(name), { name: to } satisfies ReserveTagRenameRequest),
    removeTag: (name: string): Promise<{ reserveTags: ReserveTag[] }> => remove(reserveTagPath(name)),
  },

  fieldTemplates: {
    list: (): Promise<{ templates: FieldTemplate[] }> => request(ROUTES.fieldTemplates),
    save: (template: FieldTemplateCreateRequest): Promise<{ templates: FieldTemplate[] }> =>
      post(ROUTES.fieldTemplates, template),
    remove: (id: string): Promise<{ templates: FieldTemplate[] }> => remove(fieldTemplatePath(id)),
  },

  searches: {
    list: (): Promise<{ searches: SavedSearch[] }> => request(ROUTES.searches),
    save: (name: string, query: string): Promise<{ searches: SavedSearch[] }> =>
      post(ROUTES.searches, { name, query }),
    remove: (id: string): Promise<{ searches: SavedSearch[] }> => remove(savedSearchPath(id)),
  },

  admin: {
    summary: (): Promise<{ summary: AdminSummary }> => request(ROUTES.adminSummary),

    allowlist: (): Promise<{ allowlist: AllowlistEntry[] }> => request(ROUTES.adminAllowlist),

    addToAllowlist: (email: string, note: string): Promise<{ allowlist: AllowlistEntry[] }> =>
      post(ROUTES.adminAllowlist, { email, note }),

    removeFromAllowlist: (email: string): Promise<{ allowlist: AllowlistEntry[] }> =>
      request(`${ROUTES.adminAllowlist}/${encodeURIComponent(email)}`, { method: "DELETE" }),

    accounts: (): Promise<{ accounts: AdminAccount[] }> => request(ROUTES.adminAccounts),

    setAccountStatus: (id: string, status: "active" | "disabled"): Promise<{ accounts: AdminAccount[] }> =>
      post(`${ROUTES.adminAccounts}/${encodeURIComponent(id)}/status`, { status }),

    setAccountQuota: (id: string, quotaBytes: number): Promise<{ accounts: AdminAccount[] }> =>
      post(`${ROUTES.adminAccounts}/${encodeURIComponent(id)}/quota`, { quotaBytes }),
    setAccountStock: (id: string, enabled: boolean): Promise<{ accounts: AdminAccount[] }> =>
      post(`${ROUTES.adminAccounts}/${encodeURIComponent(id)}/stock`, { enabled }),
    renameInventory: (id: string, name: string): Promise<{ accounts: AdminAccount[] }> =>
      patch(`${ROUTES.adminInventories}/${encodeURIComponent(id)}`, { name }),
    archiveInventory: (id: string, archive: boolean): Promise<{ accounts: AdminAccount[] }> =>
      post(`${ROUTES.adminInventories}/${encodeURIComponent(id)}/${archive ? "archive" : "restore"}`),
  },
};

/** "1 account", "2 accounts". A screen that says "1 accounts" reads as unfinished. */
export const plural = (count: number, one: string, many = `${one}s`): string =>
  `${count} ${count === 1 ? one : many}`;

/** Bytes as something a person reads, for the quota line. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

const BYTES_PER_GB = 1024 ** 3;

/** What a quota editor shows before anybody has touched it. */
export const bytesToGb = (bytes: number): number => bytes / BYTES_PER_GB;

/**
 * What a quota editor sends. GB because that is the unit the console already
 * shows and the unit the default (2 GB) is set in -- nobody adjusting a
 * friend's storage should have to do arithmetic in bytes. Anything that is not
 * a finite, non-negative number answers null, which the caller reads as "don't
 * submit this".
 */
export function gbToBytes(value: string): number | null {
  // `Number("")` is 0, not NaN, which would otherwise let an emptied field
  // through as a real answer instead of the missing one it is.
  if (value.trim() === "") return null;
  const gb = Number(value);
  if (!Number.isFinite(gb) || gb < 0) return null;
  return Math.round(gb * BYTES_PER_GB);
}

/**
 * Whether the storage panel should say something before the upload route has
 * to refuse one. `null` well under the limit; `"warn"` from 90% (still room to
 * finish a sitting, but worth knowing); `"error"` at or past it, which is the
 * same fact the upload route's own refusal states -- this just says it before
 * somebody's camera does.
 */
export function storageWarning(
  usedBytes: number,
  quotaBytes: number,
): { tone: "warn" | "error"; message: string } | null {
  if (usedBytes >= quotaBytes) {
    return { tone: "error", message: "You're out of storage. Delete some photos, or ask for more room." };
  }
  if (usedBytes / quotaBytes >= 0.9) {
    return {
      tone: "warn",
      message: `You're close to your storage limit: ${formatBytes(quotaBytes - usedBytes)} left.`,
    };
  }
  return null;
}
